import * as React from 'react';
import { Button, Menu, MenuItem } from '@mui/material';
import * as Icon from '@mui/icons-material/';
import { DialogConfirmacao } from './DialogConfirmacao';

interface ButtonMenuProps {
  items: {
    onClick?: () => void;
    menu: string;
    disabled?: boolean;
    children?: any[];
    /**
     * [AJUSTE 11/08/2026] Texto da pergunta de confirmação. Quando presente, o
     * onClick só é chamado depois de o usuário confirmar num diálogo.
     *
     * Existe aqui, e não em cada tela, porque este componente é a porta única de
     * 20 listagens: o "Excluir" fica logo abaixo do "Editar" no mesmo dropdown, e
     * o usuário apagava cadastro por engano. Fazendo no componente, a regra é uma
     * só e nenhuma tela pode esquecer - e uma mudança futura (texto, exigir
     * digitar o nome, log da ação) acontece num arquivo.
     *
     * Item sem `confirmar` continua funcionando como antes.
     */
    confirmar?: string;
    /**
     * [AJUSTE 21/08/2026] Titulo e rotulo do botao de confirmar, e se a acao destroi algo.
     *
     * Sem isto, TODA confirmacao herdava o padrao do dialogo - "Confirmar exclusao" com
     * botao vermelho "Excluir". Funciona para as 20 exclusoes que existem, mas em producao
     * o "Gerar Recibo" perguntou "EMITIR O RECIBO DESTA PARCELA?" sob um titulo CONFIRMAR
     * EXCLUSAO, com o botao dizendo EXCLUIR. O usuario abriu chamado, e com razao.
     *
     * Omitidos, o comportamento e o de antes.
     */
    tituloConfirmar?: string;
    rotuloConfirmar?: string;
    destrutivo?: boolean;
  }[];
  disabled?: boolean;
}

export const ButtonMenu: React.FC<ButtonMenuProps> = ({
  items,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [expanded, setExpanded] = React.useState<string[]>([]);

  // A ação fica guardada aqui entre o clique e a confirmação.
  const [confirmacao, setConfirmacao] = React.useState<{
    mensagem: string;
    titulo?: string;
    rotulo?: string;
    destrutivo?: boolean;
    acao: () => void;
  } | null>(null);

  const executarItem = (item: {
    onClick?: () => void;
    confirmar?: string;
    tituloConfirmar?: string;
    rotuloConfirmar?: string;
    destrutivo?: boolean;
  }) => {
    if (item.confirmar) {
      // O menu fecha antes: deixar o dropdown aberto atrás do diálogo confunde, e
      // o clique de fora fecharia o diálogo junto.
      setAnchorEl(null);
      setConfirmacao({
        mensagem: item.confirmar,
        titulo: item.tituloConfirmar,
        rotulo: item.rotuloConfirmar,
        destrutivo: item.destrutivo,
        acao: () => item.onClick?.(),
      });
      return;
    }

    item.onClick?.();
    handleClose();
  };

  const toggleExpanded = (menu: string) => {
    setExpanded((prev) =>
      prev.includes(menu) ? prev.filter((x) => x !== menu) : [...prev, menu]
    );
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <Button
        disabled={disabled}
        onClick={handleClick}
        id='resources-button'
        aria-controls={open ? 'resources-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
      >
        <Icon.Menu />
      </Button>
      <Menu
        id='resources-menu'
        anchorEl={anchorEl}
        open={open}
        MenuListProps={{ 'aria-labelledby': 'resources-button' }}
        onClose={handleClose}
      >
        {items.map((item, index) => (
          <React.Fragment key={index}>
            <MenuItem
              disabled={item.disabled}
              onClick={() => {
                if (item.children) {
                  toggleExpanded(item.menu);
                  return;
                }

                executarItem(item);
              }}
            >
              {item.menu}

              {item.children &&
                (expanded.includes(item.menu) ? (
                  <Icon.ExpandLess sx={{ ml: 'auto' }} />
                ) : (
                  <Icon.ExpandMore sx={{ ml: 'auto' }} />
                ))}
            </MenuItem>

            {item.children &&
              expanded.includes(item.menu) &&
              item.children.map((child, childIndex) => (
                <MenuItem
                  key={`${index}-${childIndex}`}
                  sx={{ pl: 4 }}
                  /* [08/10] O subitem não recebia `disabled`: a propriedade existia na
                     interface e era aceita sem reclamação, mas só o item de primeiro nível
                     a usava. Quem escrevesse `disabled` num filho via o item aceso e
                     clicável - uma regra declarada e silenciosamente ignorada, que é pior
                     do que não ter a regra. */
                  disabled={child.disabled}
                  onClick={() => {
                    executarItem(child);
                  }}
                >
                  {child.menu}
                </MenuItem>
              ))}
          </React.Fragment>
        ))}
      </Menu>

      <DialogConfirmacao
        aberto={confirmacao !== null}
        mensagem={confirmacao?.mensagem ?? ''}
        titulo={confirmacao?.titulo}
        textoConfirmar={confirmacao?.rotulo}
        destrutivo={confirmacao?.destrutivo}
        onCancelar={() => setConfirmacao(null)}
        onConfirmar={() => {
          const acao = confirmacao?.acao;
          setConfirmacao(null);
          acao?.();
        }}
      />
    </div>
  );
};
